import React from 'react';
import { FlightDeckClient } from '@atlassiansox/flight-deck-frontend-client';
import { createProviderWithCustomFetchData } from './create-data-provider';
import { ExperienceMark } from '../utils/experience-tracker/constants';
import { formatDescription, formatLabel } from './product-configuration-provider';
import { isFedRamp } from '@atlassian/atl-context';
import {
	type FlightDeckProductConfigurationArtifact,
	type ProductConfigurationResponse,
	type AvailableProductDetails,
	type ProductConfigurationMap,
	type ProviderResults,
} from '../../types';
import { ASSETS_APP_KEY } from '../../constants';
import messages from '../utils/messages';
import type { WithCached } from '../utils/with-cached';

const client = new FlightDeckClient('atlassian-switcher', {
	handlers: {},
	// Switch the FD default storage from localStorage to sessionStorage to avoid issues related to GDPR policy.
	// The storageEngine option isn't exposed directly by the FD SDK, but you can still pass it in
	// https://hello.jira.atlassian.cloud/browse/NAVX-658
	//@ts-ignore
	storageEngine: 'sessionStorage',
});

const { fetchMethod: fetchFlightDeck, ProviderComponent: DefaultFlightDeckProviderComponent } =
	createProviderWithCustomFetchData<ProductConfigurationResponse, {}>(
		'flightDeck',
		ExperienceMark.API_FLIGHT_DECK,
		async () => {
			const artifact: FlightDeckProductConfigurationArtifact =
				(await client.fetchConfigFromFlightDeck({
					name: 'app-switcher-fe',
					dimensions: {
						environmentType: 'prod',
						perimeter: isFedRamp() ? 'fedramp-moderate' : 'commercial',
					},
				})) as FlightDeckProductConfigurationArtifact;

			return transformFlightDeckArtifactToProductConfiguration(artifact);
		},
	);

const transformFlightDeckArtifactToProductConfiguration = (
	artifact: FlightDeckProductConfigurationArtifact,
): ProductConfigurationResponse => {
	const artifactMap = new Map<string, AvailableProductDetails>();
	for (const product of artifact.products) {
		const { apsKey, tcsProductKey, siteUrlSuffix, displayName, subtitle, ordinal, iconKey } =
			product;

		artifactMap.set(apsKey, {
			key: !!tcsProductKey ? tcsProductKey : apsKey.toLowerCase(),
			href: siteUrlSuffix,
			ordinal,
			label: formatLabel(displayName),
			description: formatDescription(subtitle),
			iconKey,
		});
		// TODO: remove this once Asset app becomes standalone app
		if (apsKey === 'JIRA_SERVICE_DESK' && !artifactMap.has(ASSETS_APP_KEY)) {
			artifactMap.set(ASSETS_APP_KEY, {
				key: ASSETS_APP_KEY,
				href: '/jira/assets',
				ordinal: ordinal + 0.1, // make sure assets is below jsm
				label: formatLabel(messages.assets),
				iconKey: 'assets',
			});
		}
	}

	return {
		products: Object.fromEntries<AvailableProductDetails>(artifactMap) as ProductConfigurationMap,
		links: Object.fromEntries(
			artifact.links.map(({ apsKey, ...link }) => [
				apsKey,
				{
					...link,
					key: apsKey.toLowerCase(),
					href: link.url,
					label: formatLabel(link.displayName),
					description: formatDescription(link.subtitle),
					iconKey: 'admin',
				},
			]),
		),
	};
};

export const FlightDeckProvider = ({
	children,
}: {
	children: (productConfiguration: ProviderResults['productConfiguration']) => React.ReactNode;
}): React.JSX.Element => (
	<DefaultFlightDeckProviderComponent>{children}</DefaultFlightDeckProviderComponent>
);

export const prefetchFlightDeck: WithCached<(param: {}) => Promise<ProductConfigurationResponse>> = fetchFlightDeck;
